import React, { useState, useEffect } from 'react';
import { wishlistAPI } from '../services/api';

const EMPTY_FORM = { name: '', cost: '', url: '' };

function WishList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      const res = await wishlistAPI.getAll();
      setItems(res.data);
    } catch (err) {
      console.error('Error fetching wish list:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        cost: formData.cost !== '' ? parseFloat(formData.cost) : null,
        url: formData.url || null,
      };
      if (editingId) await wishlistAPI.update(editingId, payload);
      else await wishlistAPI.create(payload);
      fetchItems();
      resetForm();
    } catch (err) {
      console.error('Error saving wish list item:', err);
      alert('Failed to save item');
    }
  };

  const handleEdit = (item) => {
    setFormData({
      name: item.name,
      cost: item.cost !== null ? String(item.cost) : '',
      url: item.url || '',
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleToggle = async (id) => {
    try { await wishlistAPI.toggle(id); fetchItems(); }
    catch (err) { console.error(err); }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Remove this item from your wish list?')) {
      try { await wishlistAPI.delete(id); fetchItems(); }
      catch (err) { console.error(err); }
    }
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
  };

  const set = (field) => (e) => setFormData({ ...formData, [field]: e.target.value });

  const wanted = items.filter(i => !i.purchased);
  const purchased = items.filter(i => i.purchased);

  const formatCost = (cost) => {
    if (cost === null || cost === undefined) return null;
    return `$${parseFloat(cost).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (loading) return <div className="loading"><div className="spinner"></div></div>;

  return (
    <div className="container fade-in">
      <div className="page-header">
        <div>
          <h2>Wish List</h2>
          <p>Track items you want to buy</p>
        </div>
        <button
          onClick={() => { if (showForm && !editingId) resetForm(); else setShowForm(!showForm); }}
          className="btn btn-primary"
        >
          {showForm ? 'Cancel' : '+ Add Item'}
        </button>
      </div>

      <div className="card">
        {showForm && (
          <div className="form-section">
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Item Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={set('name')}
                  placeholder="What do you want?"
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Cost</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.cost}
                    onChange={set('cost')}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Link</label>
                  <input
                    type="url"
                    className="form-input"
                    value={formData.url}
                    onChange={set('url')}
                    placeholder="https://..."
                  />
                </div>
              </div>
              <div className="flex gap-1">
                <button type="submit" className="btn btn-success">
                  {editingId ? 'Update' : 'Add'}
                </button>
                <button type="button" onClick={resetForm} className="btn btn-secondary">Cancel</button>
              </div>
            </form>
          </div>
        )}

        {items.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
              </svg>
            </div>
            <p className="empty-state-text">Your wish list is empty</p>
            <button onClick={() => setShowForm(true)} className="btn btn-primary">Add Your First Item</button>
          </div>
        ) : (
          <>
            {wanted.length > 0 && (
              <div>
                <div style={{
                  padding: '12px 24px 6px', fontSize: 12, fontWeight: 700,
                  color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em',
                }}>
                  Wanted ({wanted.length})
                </div>
                {wanted.map(item => (
                  <WishItem
                    key={item.id}
                    item={item}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onToggle={handleToggle}
                    formatCost={formatCost}
                  />
                ))}
              </div>
            )}
            {purchased.length > 0 && (
              <div>
                <div style={{
                  padding: '16px 24px 6px', fontSize: 12, fontWeight: 700,
                  color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em',
                }}>
                  Purchased ({purchased.length})
                </div>
                {purchased.map(item => (
                  <WishItem
                    key={item.id}
                    item={item}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onToggle={handleToggle}
                    formatCost={formatCost}
                    purchased
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function WishItem({ item, onEdit, onDelete, onToggle, formatCost, purchased }) {
  return (
    <div className={`list-item${purchased ? ' completed' : ''}`}>
      <input
        type="checkbox"
        checked={!!item.purchased}
        onChange={() => onToggle(item.id)}
        style={{ marginTop: 2, flexShrink: 0, cursor: 'pointer' }}
      />
      <div className="list-item-content">
        <div className="list-item-title" style={{ textDecoration: purchased ? 'line-through' : 'none' }}>
          {item.url ? (
            <a href={item.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-teal)' }}>
              {item.name}
            </a>
          ) : item.name}
        </div>
        {formatCost(item.cost) && (
          <div className="list-item-subtitle">{formatCost(item.cost)}</div>
        )}
      </div>
      <div className="list-item-actions">
        <button onClick={() => onEdit(item)} className="btn btn-secondary btn-sm">Edit</button>
        <button onClick={() => onDelete(item.id)} className="btn btn-danger btn-sm">Delete</button>
      </div>
    </div>
  );
}

export default WishList;
